import {
  AlertCircle,
  Clock,
  Edit3,
  Plus,
  Save,
  Shield,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import type { Translation } from "../../types/i18n";
import { useI18nStore } from "../../stores/i18nStore";
import { ExtendedAllergyIntolerance } from "../../types/fhir-health";

interface ExtendedAllergiesProps {
  allergies: ExtendedAllergyIntolerance[];
  onSave: (allergies: ExtendedAllergyIntolerance[]) => void;
  isEditing: boolean;
  onToggleEdit: () => void;
  isSaving: boolean;
}

// Common allergens with SNOMED CT codes for travelers
const COMMON_ALLERGENS = [
  {
    code: "387517004",
    name: "Paracetamol",
    category: "medication" as const,
    labelKey: "algAllergenParacetamol" as const,
  },
  {
    code: "387207008",
    name: "Penicillin",
    category: "medication" as const,
    labelKey: "algAllergenPenicillin" as const,
  },
  {
    code: "387467008",
    name: "Aspirin",
    category: "medication" as const,
    labelKey: "algAllergenAspirin" as const,
  },
  {
    code: "387137007",
    name: "Amoxicillin",
    category: "medication" as const,
    labelKey: "algAllergenAmoxicillin" as const,
  },
  {
    code: "396064000",
    name: "Latex",
    category: "environment" as const,
    labelKey: "algAllergenLatex" as const,
  },
  {
    code: "424213003",
    name: "Pollen",
    category: "environment" as const,
    labelKey: "algAllergenPollen" as const,
  },
  {
    code: "227493005",
    name: "Nuts",
    category: "food" as const,
    labelKey: "algAllergenNuts" as const,
  },
  {
    code: "102263004",
    name: "Eggs",
    category: "food" as const,
    labelKey: "algAllergenEggs" as const,
  },
  {
    code: "3718001",
    name: "Cow's milk",
    category: "food" as const,
    labelKey: "algAllergenCowsMilk" as const,
  },
  {
    code: "44027008",
    name: "Seafood",
    category: "food" as const,
    labelKey: "algAllergenSeafood" as const,
  },
  {
    code: "102264005",
    name: "Chocolate",
    category: "food" as const,
    labelKey: "algAllergenChocolate" as const,
  },
  {
    code: "762952008",
    name: "Peanuts",
    category: "food" as const,
    labelKey: "algAllergenPeanuts" as const,
  },
  {
    code: "91935009",
    name: "Bee stings",
    category: "biologic" as const,
    labelKey: "algAllergenBeeStings" as const,
  },
  {
    code: "424648006",
    name: "Wasp stings",
    category: "biologic" as const,
    labelKey: "algAllergenWaspStings" as const,
  },
  {
    code: "39579001",
    name: "Anesthetics",
    category: "medication" as const,
    labelKey: "algAllergenAnesthetics" as const,
  },
  {
    code: "387458008",
    name: "Ibuprofen",
    category: "medication" as const,
    labelKey: "algAllergenIbuprofen" as const,
  },
];

const LEGACY_TEXT_MAP: Record<string, string> = {
  Nüsse: "Nuts",
  Eier: "Eggs",
  Kuhimilch: "Cow's milk",
  Meeresfrüchte: "Seafood",
  Schokolade: "Chocolate",
  Erdnüsse: "Peanuts",
  Bienenstiche: "Bee stings",
  Wespenstiche: "Wasp stings",
  Anästhetika: "Anesthetics",
  Kuhmilch: "Cow's milk",
};

type AllergyCategory = ExtendedAllergyIntolerance["category"];

const CATEGORY_KEYS: Record<string, keyof Translation> = {
  food: "algCatFood",
  medication: "algCatMedication",
  environment: "algCatEnvironment",
  biologic: "algCatBiologic",
};

const categoryLabel = (t: Translation, category?: AllergyCategory | string) =>
  t[CATEGORY_KEYS[category ?? ""] ?? "algCatBiologic"] as string;

/** Display name for a stored allergy: the translated label when the record
 * still carries the list's own name for its code (legacy German included),
 * otherwise the stored text. */
const allergyLabel = (
  t: Translation,
  allergy: Pick<ExtendedAllergyIntolerance, "code" | "display">,
) => {
  const stored = translateLegacyLabel(allergy.display);
  const known = COMMON_ALLERGENS.find((a) => a.code === allergy.code);
  return known && (!stored || stored === known.name)
    ? t[known.labelKey]
    : stored;
};

const translateLegacyLabel = (value?: string) => {
  if (!value) return "";
  return LEGACY_TEXT_MAP[value] || value;
};

export const ExtendedAllergies: React.FC<ExtendedAllergiesProps> = ({
  allergies,
  onSave,
  isEditing,
  onToggleEdit,
  isSaving,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [localAllergies, setLocalAllergies] =
    useState<ExtendedAllergyIntolerance[]>(allergies);

  useEffect(() => {
    setLocalAllergies(
      allergies.map((allergy) => ({
        ...allergy,
        display: translateLegacyLabel(allergy.display),
      })),
    );
  }, [allergies]);

  const fieldClassName =
    "w-full h-12 px-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent";

  const addNewAllergy = () => {
    const newAllergy: ExtendedAllergyIntolerance = {
      id: crypto.randomUUID(),
      code: "",
      display: "",
      category: "food",
      criticality: "low",
      clinicalStatus: "active",
      onsetDateTime: "",
      reactions: [],
    };
    setLocalAllergies([...localAllergies, newAllergy]);
  };

  const updateAllergy = (
    index: number,
    field: keyof ExtendedAllergyIntolerance,
    value: string | boolean | Date,
  ) => {
    const updated = [...localAllergies];
    const current = updated[index];

    if (field === "code") {
      // Auto-fill allergen name and category
      const allergen = COMMON_ALLERGENS.find((a) => a.code === value);
      if (allergen && current) {
        updated[index] = {
          ...current,
          code: value,
          display: allergen.name,
          category: allergen.category,
        } as ExtendedAllergyIntolerance;
      } else if (current) {
        updated[index] = {
          ...current,
          [field]: value,
        } as ExtendedAllergyIntolerance;
      }
    } else if (current) {
      updated[index] = {
        ...current,
        [field]: value,
      } as ExtendedAllergyIntolerance;
    }
    setLocalAllergies(updated);
  };

  const addReaction = (allergyIndex: number) => {
    const updated = [...localAllergies];
    const current = updated[allergyIndex];
    if (current) {
      const newReaction = {
        manifestation: [""],
        severity: "mild" as const,
        note: "",
      };
      updated[allergyIndex] = {
        ...current,
        reactions: [...(current.reactions || []), newReaction],
      } as ExtendedAllergyIntolerance;
      setLocalAllergies(updated);
    }
  };

  const updateReaction = (
    allergyIndex: number,
    reactionIndex: number,
    field: string,
    value: string | boolean,
  ) => {
    const updated = [...localAllergies];
    const current = updated[allergyIndex];
    if (current && current.reactions) {
      const updatedReactions = [...current.reactions];
      const existingReaction = updatedReactions[reactionIndex];

      if (!existingReaction) return;

      if (field === "manifestation") {
        updatedReactions[reactionIndex] = {
          manifestation: [String(value)],
          ...(existingReaction.severity !== undefined && {
            severity: existingReaction.severity,
          }),
          ...(existingReaction.onset !== undefined && {
            onset: existingReaction.onset,
          }),
          ...(existingReaction.note !== undefined && {
            note: existingReaction.note,
          }),
        };
      } else if (field === "severity") {
        updatedReactions[reactionIndex] = {
          manifestation: existingReaction.manifestation,
          ...(typeof value === "string" && {
            severity: value as "mild" | "moderate" | "severe",
          }),
          ...(existingReaction.onset !== undefined && {
            onset: existingReaction.onset,
          }),
          ...(existingReaction.note !== undefined && {
            note: existingReaction.note,
          }),
        };
      } else if (field === "note") {
        updatedReactions[reactionIndex] = {
          manifestation: existingReaction.manifestation,
          ...(existingReaction.severity !== undefined && {
            severity: existingReaction.severity,
          }),
          ...(existingReaction.onset !== undefined && {
            onset: existingReaction.onset,
          }),
          ...(typeof value === "string" && value ? { note: value } : {}),
        };
      } else if (field === "onset") {
        updatedReactions[reactionIndex] = {
          manifestation: existingReaction.manifestation,
          ...(existingReaction.severity !== undefined && {
            severity: existingReaction.severity,
          }),
          ...(typeof value === "string" && value ? { onset: value } : {}),
          ...(existingReaction.note !== undefined && {
            note: existingReaction.note,
          }),
        };
      }

      updated[allergyIndex] = {
        ...current,
        reactions: updatedReactions,
      };
      setLocalAllergies(updated);
    }
  };

  const removeReaction = (allergyIndex: number, reactionIndex: number) => {
    const updated = [...localAllergies];
    const current = updated[allergyIndex];
    if (current && current.reactions) {
      const updatedReactions = current.reactions.filter(
        (_, i) => i !== reactionIndex,
      );
      updated[allergyIndex] = {
        ...current,
        reactions: updatedReactions,
      } as ExtendedAllergyIntolerance;
      setLocalAllergies(updated);
    }
  };

  const removeAllergy = (index: number) => {
    const updated = localAllergies.filter((_, i) => i !== index);
    setLocalAllergies(updated);
  };

  const handleSave = () => {
    onSave(localAllergies);
  };

  const getCriticalityColor = (criticality?: string) => {
    switch (criticality) {
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      case "low":
        return "text-green-600 bg-green-50 border-green-200";
      default:
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
    }
  };

  const getCriticalityIcon = (criticality?: string) => {
    switch (criticality) {
      case "high":
        return <AlertCircle className="w-4 h-4" />;
      case "low":
        return <Shield className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case "severe":
        return "text-red-600";
      case "moderate":
        return "text-yellow-600";
      default:
        return "text-green-600";
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span>{t.allergiesAndIntolerances}</span>
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={onToggleEdit}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title={isEditing ? t.cancel : t.edit}
            aria-label={isEditing ? t.cancel : t.edit}
          >
            <Edit3 className="w-4 h-4" />
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              ) : (
                <Save className="w-3 h-3" />
              )}
              <span>{isSaving ? t.saving : t.save}</span>
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-6">
          {localAllergies.map((allergy, index) => (
            <div
              key={allergy.id || index}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label
                    htmlFor={`allergy-${index}-code`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.algAllergenLabel}
                  </label>
                  <select
                    id={`allergy-${index}-code`}
                    value={allergy.code}
                    onChange={(e) =>
                      updateAllergy(index, "code", e.target.value)
                    }
                    className={fieldClassName}
                  >
                    <option value="">{t.pleaseSelect}</option>
                    {COMMON_ALLERGENS.map((allergen) => (
                      <option key={allergen.code} value={allergen.code}>
                        {t[allergen.labelKey]} (
                        {categoryLabel(t, allergen.category)})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`allergy-${index}-category`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.category}
                  </label>
                  <select
                    id={`allergy-${index}-category`}
                    value={allergy.category}
                    onChange={(e) =>
                      updateAllergy(index, "category", e.target.value)
                    }
                    className={fieldClassName}
                  >
                    <option value="food">{t.algCatFood}</option>
                    <option value="medication">{t.algCatMedication}</option>
                    <option value="environment">{t.algCatEnvironment}</option>
                    <option value="biologic">{t.algCatBiologic}</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`allergy-${index}-criticality`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.algCriticality}
                  </label>
                  <select
                    id={`allergy-${index}-criticality`}
                    value={allergy.criticality}
                    onChange={(e) =>
                      updateAllergy(index, "criticality", e.target.value)
                    }
                    className={fieldClassName}
                  >
                    <option value="low">{t.algCritLow}</option>
                    <option value="high">{t.algCritHigh}</option>
                    <option value="unable-to-assess">{t.algCritUnknown}</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`allergy-${index}-status`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.status}
                  </label>
                  <select
                    id={`allergy-${index}-status`}
                    value={allergy.clinicalStatus}
                    onChange={(e) =>
                      updateAllergy(index, "clinicalStatus", e.target.value)
                    }
                    className={fieldClassName}
                  >
                    <option value="active">{t.active}</option>
                    <option value="inactive">{t.inactive}</option>
                    <option value="resolved">{t.algStatusResolved}</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`allergy-${index}-onset`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.algOnsetLabel}
                  </label>
                  <input
                    id={`allergy-${index}-onset`}
                    type="date"
                    value={allergy.onsetDateTime}
                    onChange={(e) =>
                      updateAllergy(index, "onsetDateTime", e.target.value)
                    }
                    className={fieldClassName}
                  />
                </div>
              </div>

              {/* Reactions */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    {t.algReactions}
                  </h4>
                  <button
                    onClick={() => addReaction(index)}
                    className="text-xs text-primary-600 hover:text-primary-800"
                  >
                    {t.algAddReaction}
                  </button>
                </div>

                {allergy.reactions &&
                  allergy.reactions.map((reaction, reactionIndex) => (
                    <div
                      key={reactionIndex}
                      className="border border-gray-200 rounded p-3 mb-2"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label
                            htmlFor={`allergy-${index}-reaction-${reactionIndex}-manifestation`}
                            className="block text-xs font-medium text-gray-600 mb-1"
                          >
                            {t.algSymptoms}
                          </label>
                          <input
                            id={`allergy-${index}-reaction-${reactionIndex}-manifestation`}
                            type="text"
                            value={reaction.manifestation[0] || ""}
                            onChange={(e) =>
                              updateReaction(
                                index,
                                reactionIndex,
                                "manifestation",
                                e.target.value,
                              )
                            }
                            className="w-full h-10 px-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary-500"
                            placeholder={t.algSymptomsPlaceholder}
                          />
                        </div>

                        <div>
                          <label
                            htmlFor={`allergy-${index}-reaction-${reactionIndex}-severity`}
                            className="block text-xs font-medium text-gray-600 mb-1"
                          >
                            {t.algSeverity}
                          </label>
                          <select
                            id={`allergy-${index}-reaction-${reactionIndex}-severity`}
                            value={reaction.severity}
                            onChange={(e) =>
                              updateReaction(
                                index,
                                reactionIndex,
                                "severity",
                                e.target.value,
                              )
                            }
                            className="w-full h-10 px-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="mild">{t.algSevMild}</option>
                            <option value="moderate">{t.algSevModerate}</option>
                            <option value="severe">{t.algSevSevere}</option>
                          </select>
                        </div>

                        <div className="flex items-end">
                          <button
                            onClick={() => removeReaction(index, reactionIndex)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title={t.algRemoveReaction}
                            aria-label={t.algRemoveReaction}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-2">
                        <label
                          htmlFor={`allergy-${index}-reaction-${reactionIndex}-note`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          {t.algReactionNotes}
                        </label>
                        <textarea
                          id={`allergy-${index}-reaction-${reactionIndex}-note`}
                          value={reaction.note || ""}
                          onChange={(e) =>
                            updateReaction(
                              index,
                              reactionIndex,
                              "note",
                              e.target.value,
                            )
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary-500"
                          rows={2}
                          placeholder={t.algReactionNotesPlaceholder}
                        />
                      </div>
                    </div>
                  ))}
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => removeAllergy(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title={t.algRemoveAllergy}
                  aria-label={t.algRemoveAllergy}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addNewAllergy}
            className="w-full px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t.algAddNew}</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {allergies.length === 0 ? (
            <p className="text-gray-500 italic">{t.noAllergiesDocumented}</p>
          ) : (
            allergies.map((allergy, index) => {
              const criticalityColor = getCriticalityColor(allergy.criticality);
              const criticalityIcon = getCriticalityIcon(allergy.criticality);

              return (
                <div
                  key={allergy.id || index}
                  className={`border rounded-lg p-4 ${criticalityColor}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                          {criticalityIcon}
                          <span>{allergyLabel(t, allergy)}</span>
                        </h4>
                        <div className="text-xs px-2 py-1 rounded bg-white bg-opacity-50">
                          {categoryLabel(t, allergy.category)}
                        </div>
                      </div>

                      <div className="mt-2 text-sm">
                        <span
                          className={`font-medium ${
                            allergy.criticality === "high"
                              ? "text-red-700"
                              : allergy.criticality === "low"
                                ? "text-green-700"
                                : "text-yellow-700"
                          }`}
                        >
                          {allergy.criticality === "high"
                            ? t.algCritHighBadge
                            : allergy.criticality === "low"
                              ? t.algCritLowBadge
                              : t.algCritUnknownBadge}
                        </span>
                        {allergy.onsetDateTime && (
                          <span className="ml-3 text-gray-600">
                            {t.algSince} {fmt.date(allergy.onsetDateTime)}
                          </span>
                        )}
                      </div>

                      {allergy.reactions && allergy.reactions.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-xs font-medium text-gray-600 mb-1">
                            {t.algKnownReactions}
                          </h5>
                          {allergy.reactions.map((reaction, reactionIndex) => (
                            <div
                              key={reactionIndex}
                              className="text-sm text-gray-700 flex items-center justify-between"
                            >
                              <span>{reaction.manifestation[0]}</span>
                              <span
                                className={`text-xs font-medium ${getSeverityColor(reaction.severity)}`}
                              >
                                {reaction.severity === "severe"
                                  ? t.algSevSevere
                                  : reaction.severity === "moderate"
                                    ? t.algSevModerate
                                    : t.algSevMild}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
